<script setup lang="ts">
import { InfoCircle, SearchMd } from "utruckin-icons";
import CommonDropdown from "@/components/common/CommonDropdown.vue";
import CommonSearch from "@/components/common/CommonSearch.vue";
import CommonTable from "@/components/common/CommonTable.vue";
import { useTeamStore } from "@/features/teams/team.store";
import { computed, onMounted, ref, watch } from "vue";
import { ITeam } from "@/features/teams/team.types";
import { DateTime } from "luxon";
import { ICompany } from "@/features/company/company.types";
import { useDriverStore } from "@/features/drivers/drivers.store";
import { useNotification } from "@/utils/notification";
import CommonModal from "@/components/common/CommonModal.vue";
import CommonButton from "@/components/common/CommonButton.vue";
import CommonInput from "@/components/common/CommonInput.vue";
import { useAppStore } from "@/store/app.store";

const teamStore = useTeamStore();
const driverStore = useDriverStore();
const appStore = useAppStore();

const { pushNotification } = useNotification();

const teams = ref<ITeam[]>([]);
const team = ref<string | null>(null);
const driverLoading = ref(false);
const search = ref<string>("");
const drivers = ref<ICompany[]>([]);

const isOpenModal = ref<boolean>(false);
const note = ref<string>("");
const modalItem = ref<ICompany | null>(null);

let searchTimer: ReturnType<typeof setTimeout> | null = null;

const openModal = (item: ICompany) => {
  modalItem.value = item;
  isOpenModal.value = true;
  note.value = item.note;
};

const closeModal = () => {
  modalItem.value = null;
  isOpenModal.value = false;
  note.value = "";
};

const saveModal = async () => {
  if (!modalItem.value) return;

  await driverStore.addDriverNote(
    driverLoading,
    modalItem.value.id,
    { note: note.value },
    async (data: any) => {
      if (data) {
        pushNotification("Successfully unarchived", "success");
        closeModal();
        await getList();
      }
    }
  );
};

const teamItems = computed(() =>
  teams.value.map((t) => ({ value: t.id, name: t.name }))
);

const getTeamName = (teamId: string | null): string => {
  if (!teamId) return "—";
  const found = teams.value.find((t) => t.id === teamId);
  return found?.name || "—";
};

function datepickerToUtcDateOnly(d: Date | null | undefined) {
  return DateTime.fromISO(d, {
    zone: "utc",
  })
    .setZone("America/New_York")
    .toFormat("yyyy-MM-dd");
}

const getList = async () => {
  const teamLoading = ref(false);
  [teams.value, drivers.value] = await Promise.all([
    teamStore.getTeams(teamLoading),
    driverStore.getDrivers(driverLoading),
  ]);
};

onMounted(() => {
  getList();
});

watch(search, (val) => {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(async () => {
    driverStore.commonFilter.search = val;
    await getList();
  }, 400);
});

watch(team, async (val) => {
  driverStore.commonFilter.team_id = val as string;
  await getList();
});

watch(
  () => appStore.refreshKey,
  () => {
    getList();
  },
  {
    immediate: false,
  }
);
</script>

<template>
  <div class="v-driver">
    <div class="header">
      <CommonSearch
        v-model="search"
        :icon="SearchMd"
        placeholder="Search"
        clearable
      />
      <CommonDropdown
        v-model="team"
        :items="teamItems"
        label="Team"
        clearable
      />
    </div>

    <div class="body">
      <CommonTable
        :headers="driverStore.driverHeaders"
        :loading="driverLoading"
        :items="drivers.items"
      >
        <template #id="{ index }">
          <span>{{ index + 1 }}</span>
        </template>

        <template #eld_last_activity_at="{ item }">
          {{ datepickerToUtcDateOnly(item.eld_last_activity_at) }}
        </template>

        <template #team="{ item }">
          <span>{{ getTeamName(item.team_id) }}</span>
        </template>

        <template #actions="{ item }">
          <div class="action" @click="openModal(item)">
            <InfoCircle :size="18" />
          </div>
        </template>
      </CommonTable>
    </div>

    <CommonModal v-model="isOpenModal" title="Driver note">
      <div class="new-modal">
        <CommonInput v-model="note" label="Note" multiline />
      </div>

      <template #footer>
        <div class="description__footer">
          <CommonButton type="outline" label="Cancel" @clicked="closeModal" />
          <CommonButton type="primary" label="Save" @clicked="saveModal" />
        </div>
      </template>
    </CommonModal>
  </div>
</template>

<style scoped lang="scss">
.v-driver {
  height: calc(100vh - 80px);
  overflow: auto;
  background: var(--color-system-background);

  .header {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 16px;
  }

  .body {
    padding: 0 16px;

    .action {
      width: 32px;
      height: 32px;
      border: 1px solid var(--color-border);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--color-secondary-system-background);
    }
  }
}

.description__footer {
  width: 100%;
  gap: var(--base-space-8);
  display: flex;
  align-items: center;
  justify-content: end;
}
</style>
